import { useState } from 'react';
import {
  AppBar,
  Box,
  ButtonBase,
  CircularProgress,
  Container,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import KeyIcon from '@mui/icons-material/VpnKey';
import { Link as RouterLink, Route, Routes, useLocation } from 'react-router-dom';
import type { AuthUser } from 'shared';
import { ROLE_LABEL, useAuth, useLogout } from './auth';
import { AlertBell } from './components/AlertBell';
import { ChangePasswordDialog } from './components/ChangePasswordDialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuditPage } from './pages/AuditPage';
import { BaoCaoPage } from './pages/BaoCaoPage';
import { RegistryPage } from './pages/RegistryPage';
import { MustChangePasswordPage } from './pages/MustChangePasswordPage';
import { OpsPage } from './pages/OpsPage';
import { TrashPage } from './pages/TrashPage';
import { PageDetailPage } from './pages/PageDetailPage';
import { RaSoatPage } from './pages/RaSoatPage';
import { LoginPage } from './pages/LoginPage';
import { UsersPage } from './pages/UsersPage';
import { MONO, TOPBAR_H, tokens } from './theme';

/**
 * Menu tài khoản ở góc phải thanh trên cùng.
 *
 * Điều hướng của ứng dụng nằm ở thanh ngang, KHÔNG phải sidebar: hiện chỉ có một
 * đích chính (danh sách trang), mà màn chi tiết đã có một cột bên trái là thanh 7
 * bước — thêm sidebar nữa thành hai cột trái không rõ cái nào là gì. Khi có từ 5
 * đích trở lên (Chờ duyệt, Cài đặt, Báo cáo…) thì mới chuyển sang sidebar, và chỉ
 * phải sửa đúng file này.
 */
function AccountMenu({ user }: { user: AuthUser }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pwOpen, setPwOpen] = useState(false);
  const logout = useLogout();

  const initials = user.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ borderRadius: '999px', pl: 0.5, pr: 1, py: 0.4, gap: 1, color: '#CFE2E8' }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: '#2B5570',
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          {initials}
        </Box>
        <Typography sx={{ fontSize: 13.5, display: { xs: 'none', sm: 'block' } }}>
          {user.name}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 18 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
      >
        <Box sx={{ px: 2, py: 1.2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{user.name}</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{user.email}</Typography>
          <Typography sx={{ fontSize: 12, color: tokens.teal, fontWeight: 600, mt: 0.4 }}>
            {ROLE_LABEL[user.role]}
          </Typography>
        </Box>
        <Divider />

        {/* Nhắc lại các đích trong menu tài khoản: trên màn hình hẹp, thanh điều
            hướng ngang bị ẩn nên đây là đường duy nhất tới chúng. */}
        <MenuItem component={RouterLink} to="/bao-cao" onClick={() => setAnchor(null)}>
          <ListItemIcon>
            <AssessmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Báo cáo</ListItemText>
        </MenuItem>

        {user.role === 'WEB_ADMINISTRATOR' && [
          <MenuItem key="u" component={RouterLink} to="/nguoi-dung" onClick={() => setAnchor(null)}>
            <ListItemIcon>
              <GroupIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Người dùng</ListItemText>
          </MenuItem>,
          <MenuItem key="a" component={RouterLink} to="/nhat-ky" onClick={() => setAnchor(null)}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Nhật ký kiểm toán</ListItemText>
          </MenuItem>,
          <MenuItem key="o" component={RouterLink} to="/van-hanh" onClick={() => setAnchor(null)}>
            <ListItemIcon>
              <MonitorHeartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Vận hành</ListItemText>
          </MenuItem>,
        ]}

        <MenuItem
          onClick={() => {
            setAnchor(null);
            setPwOpen(true);
          }}
        >
          <ListItemIcon>
            <KeyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Đổi mật khẩu</ListItemText>
        </MenuItem>

        <Divider />
        {/* `useLogout` tự tải lại trang về `/` nên không cần điều hướng ở đây; điều
            hướng bằng router sẽ bị chính lần tải lại đó ghi đè. */}
        <MenuItem onClick={() => logout.mutate()} disabled={logout.isPending}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { fontSize: 14 } }}>
            {logout.isPending ? 'Đang đăng xuất…' : 'Đăng xuất'}
          </ListItemText>
        </MenuItem>
      </Menu>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}

/**
 * Mục điều hướng trên thanh trên cùng, tô sáng khi đang ở đúng màn đó.
 *
 * Đây là chỗ trước đây thiếu hẳn: vào `/nguoi-dung` rồi thì không có đường nào quay
 * lại danh sách trang ngoài việc tự sửa đường dẫn trên trình duyệt.
 */
function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  const active = to === '/' ? pathname === '/' || pathname.startsWith('/pages/') : pathname === to;

  return (
    <ButtonBase
      component={RouterLink}
      to={to}
      sx={{
        px: 1.4,
        py: 0.7,
        borderRadius: '8px',
        fontSize: 13.5,
        fontWeight: active ? 600 : 400,
        color: active ? '#fff' : '#9FC2CD',
        bgcolor: active ? tokens.ink2 : 'transparent',
        '&:hover': { bgcolor: tokens.ink2, color: '#fff' },
      }}
    >
      {children}
    </ButtonBase>
  );
}

function TopBar({ user }: { user: AuthUser }) {
  return (
    <AppBar position="sticky" elevation={0} sx={{ bgcolor: tokens.ink }}>
      <Toolbar sx={{ minHeight: TOPBAR_H, height: TOPBAR_H, gap: 1.5 }}>
        {/* Cả khối thương hiệu bấm được, về trang chủ — thói quen của mọi ứng dụng web,
            và là đường thoát khi người dùng lạc ở màn con. */}
        <ButtonBase
          component={RouterLink}
          to="/"
          sx={{ gap: 1.5, borderRadius: '8px', px: 0.5, py: 0.4, textAlign: 'left' }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              bgcolor: tokens.mint,
              color: tokens.ink,
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: 13,
              flex: 'none',
            }}
          >
            AI
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.2, color: '#fff' }}>
              SEO Copilot
            </Typography>
            <Typography sx={{ fontSize: 11, letterSpacing: '.06em', color: '#8FB3BD' }}>
              SPLUS SOFTWARE
            </Typography>
          </Box>
        </ButtonBase>

        <Stack direction="row" spacing={0.5} sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }}>
          <NavLink to="/">Trang của tôi</NavLink>
          <NavLink to="/bao-cao">Báo cáo</NavLink>
          <NavLink to="/ra-soat">Rà soát</NavLink>
          <NavLink to="/thung-rac">Thùng rác</NavLink>
          {user.role === 'WEB_ADMINISTRATOR' && (
            <>
              <NavLink to="/nguoi-dung">Người dùng</NavLink>
              <NavLink to="/nhat-ky">Nhật ký</NavLink>
              <NavLink to="/van-hanh">Vận hành</NavLink>
            </>
          )}
        </Stack>

        <Box flex={1} />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              fontFamily: MONO,
              fontSize: 11.5,
              color: '#B9D0D8',
              bgcolor: tokens.ink2,
              px: 1.4,
              py: 0.5,
              borderRadius: 999,
            }}
          >
            giai đoạn 1 · chỉ đề xuất
          </Box>
          {/* Chuông đặt cạnh menu tài khoản, hiện ở MỌI kích thước màn hình: thanh
              điều hướng bị ẩn trên màn hẹp, nhưng cảnh báo thì không được ẩn. */}
          <AlertBell />
          <AccountMenu user={user} />
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export function App() {
  const { user, loading } = useAuth();

  // Chưa biết đã đăng nhập chưa thì không dựng gì: nháy màn đăng nhập rồi lại nhảy
  // sang danh sách trang là cảm giác hỏng, dù kỹ thuật thì đúng.
  if (loading) {
    return (
      <Stack sx={{ minHeight: '100dvh' }} alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (!user) return <LoginPage />;

  // Đang dùng mật khẩu tạm thì KHÔNG dựng ứng dụng, kể cả thanh điều hướng
  // (spec SC-09-C). Đây chỉ là lớp thuận tiện — API cũng chặn bằng guard.
  if (user.mustChangePassword) return <MustChangePasswordPage user={user} />;

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <TopBar user={user} />
      <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 }, px: { xs: 2, md: 4 } }}>
        {/* Bọc phần nội dung chứ không bọc cả khung: một lỗi trong màn con thì
            thanh trên cùng vẫn còn, người dùng còn đường đi chỗ khác thay vì
            nhìn một trang trắng (spec SC-16 §4). */}
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<RegistryPage />} />
            <Route path="/pages/:slug" element={<PageDetailPage />} />
            {/* Ba màn dưới tự chặn theo vai trò bên trong — server cũng chặn ở API,
                nên người không phải admin gõ thẳng đường dẫn cũng không lấy được
                dữ liệu. */}
            <Route path="/nguoi-dung" element={<UsersPage />} />
            <Route path="/nhat-ky" element={<AuditPage />} />
            <Route path="/van-hanh" element={<OpsPage />} />
            {/* Thùng rác mở cho MỌI vai trò: ai xoá được trang thì phải khôi phục
                được trang đó. Máy chủ vẫn lọc theo chủ sở hữu (spec SC-03). */}
            <Route path="/thung-rac" element={<TrashPage />} />
            {/* Rà soát cả danh sách (spec SC-17, SC-19) — mọi vai trò vào được,
                máy chủ lọc theo phạm vi như mọi đường đọc khác. */}
            <Route path="/ra-soat" element={<RaSoatPage />} />
            {/* Báo cáo tổng hợp (spec SC-08) — mọi vai trò vào được, máy chủ lọc
                theo phạm vi. Riêng ô đặt ngưỡng cảnh báo bên trong chỉ hiện với
                Web Administrator, và API cũng chặn ở đó. */}
            <Route path="/bao-cao" element={<BaoCaoPage />} />
          </Routes>
        </ErrorBoundary>
      </Container>
    </Box>
  );
}
